import { useNavigate } from 'react-router-dom';

// Maps old screen-name strings to URL paths.
// Preserves the existing navigate(screenName, data) call-site API so no
// .web.jsx / .mobile.jsx files need to change.
const ROUTES = {
  'reception':        ()     => '/reception',
  // No 'patients' key: the patient LIST page was removed. The profile below stays —
  // it is reached from پذیرش and the visit-history screen, not from a list.
  // Optional data.tab deep-links a record section (/patients/:id/:tab).
  'patient-detail':   (data) => `/patients/${data?.patientId || data?.id}${data?.tab ? `/${data.tab}` : ''}`,
  'encounters':       ()     => '/encounters',
  // «جست‌وجوی بیمار» — the phone's way to a patient who is not on today's
  // list. No query parameter: a name in the address is PHI in the history and
  // in every shared link, so the term stays in the screen's own state.
  'lookup':           ()     => '/lookup',
  // Optional data.c deep-links one conversation (/messages?c=<roomId>). The room
  // id is opaque, so it is safe in a URL — no PHI ever goes there.
  'messages':         (data) => `/messages${data?.c ? `?c=${data.c}` : ''}`,
  'visit':            (data) => `/visit/${data?.patientId || data?.id}`,
  'voice':            (data) => `/voice/${data?.patientId || data?.id}`,
  'prescription':     (data) => `/prescription/${data?.patientId || data?.id}`,
  // Optional data.tab deep-links a settings section (/profile?tab=credits).
  'profile':          (data) => `/profile${data?.tab ? `?tab=${data.tab}` : ''}`,
  'credits':          ()     => '/credits',
  // Every id the sidebar can dispatch must be here: navigate() silently returns on an
  // unknown key, so a missing entry makes the nav item do nothing at all — no error,
  // no route change. /accounting existed as a <Route> and a screen the whole time; only
  // this line was missing, so حسابداری was reachable by URL but dead in the menu.
  'accounting':       ()     => '/accounting',
  // «صفحهٔ من» — the doctor's public page builder (docs/DOCTOR_PUBLIC_PAGE_DESIGN.md §12).
  'my-page':          ()     => '/my-page',
  // No 'login': sign-in is Keycloak-hosted, and the two in-app login screens
  // this key pointed at predated the PKCE migration — nothing had navigated
  // here since, and the pair were live bait for the next person.
  // Removed: calendar / reports / inventory / settings / register-doctor. None of
  // them has a <Route> in App.jsx — they fell through the '*' catch-all to
  // پذیرش, which read as "the button randomly goes home". If one of these
  // screens ships, add its <Route> and its entry here together.
};

export function useAppNavigate() {
  const rrNavigate = useNavigate();
  return (to, data = null) => {
    const path = ROUTES[to]?.(data);
    if (!path) {
      // Returning quietly is what made the missing 'accounting' entry so hard to see:
      // the menu item just did nothing. Say so.
      console.warn(`[useAppNavigate] no route for "${to}" — add it to ROUTES`);
      return;
    }
    rrNavigate(path, data ? { state: data } : undefined);
    window.scrollTo(0, 0);
  };
}
